import type { MarketTimelineState } from "./features/marketTimeline/types";

export const OUTCOMES = [
  "Nedvihol",
  "Dohodnuté stretnutie",
  "Zavolať neskôr",
  "Nezáujem",
  "Rozmyslí si",
  "Predáva sám"
] as const;

export type CallOutcome = (typeof OUTCOMES)[number];

export const TRANSACTION_TYPES = ["Predaj", "Kúpa", "Prenájom", "Nájom", "Investícia", "Iné"] as const;

export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const PROPERTY_TYPES = [
  "Byt",
  "Rodinný dom",
  "Pozemok",
  "Rekreačná nehnuteľnosť",
  "Komerčný priestor",
  "Administratívny priestor",
  "Garáž",
  "Novostavba",
  "Developerský projekt",
  "Iné"
] as const;

export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const APARTMENT_ROOM_OPTIONS = [
  "Garsónka",
  "1-izbový",
  "2-izbový",
  "3-izbový",
  "4-izbový",
  "5 a viac izbový"
] as const;

export type ApartmentRooms = (typeof APARTMENT_ROOM_OPTIONS)[number];

export type CaseLocationSource = "dataset" | "custom";

export const COOPERATION_TYPES = ["exclusive", "nonExclusive", "unspecified"] as const;

export type CooperationType = (typeof COOPERATION_TYPES)[number];

export const CASE_PRIORITIES = ["Bežný", "HOT"] as const;

export type CasePriority = (typeof CASE_PRIORITIES)[number];

export const STAGES = [
  "Nový",
  "Prvý cold call",
  "Kontaktovaný",
  "Follow-up",
  "Stretnutie",
  "Obhliadka",
  "Rokovanie",
  "Aktívne",
  "Zainzerované",
  "Rezervované",
  "Predané",
  "Uzavretý",
  "Odmietnutý",
  "Čakajúci",
  "Vyradený",
  "Rozmýšľa",
  "Stratené"
] as const;

export type CaseStage = (typeof STAGES)[number];

export const CASE_STAGE_OPTIONS: readonly CaseStage[] = [
  "Nový",
  "Prvý cold call",
  "Kontaktovaný",
  "Follow-up",
  "Stretnutie",
  "Aktívne",
  "Zainzerované",
  "Rezervované",
  "Predané",
  "Rozmýšľa",
];

export type CaseStatus =
  | "Nový"
  | "Prvý cold call"
  | "Kontaktovaný"
  | "Follow-up"
  | "Nedvihol"
  | "Stretnutie"
  | "Stretnutie dohodnuté"
  | "Zavolať neskôr"
  | "Rozmýšľa"
  | "Predáva sám"
  | "Obhliadka"
  | "Rokovanie"
  | "Aktívne"
  | "Zainzerované"
  | "Rezervované"
  | "Predané"
  | "Uzavretý"
  | "Odmietnutý"
  | "Čakajúci"
  | "Vyradený"
  | "Nezáujem"
  | "Stratené";

export type FollowUp = {
  date: string;
  time: string;
  endTime?: string;
  purpose: string;
  workflowId?: "listing" | "reservation";
  actionId?: "advertising_report" | "market_review" | "market_review_recurring" | "reservation_expiry";
};

export const OWNER_SALE_SEQUENCE_END_REASONS = [
  "Získaný nábor",
  "Nechce ďalší kontakt",
  "Nehnuteľnosť predaná",
  "Kontakt nedostupný",
  "Sekvencia ukončená manuálne"
] as const;

export type OwnerSaleSequenceEndReason = (typeof OWNER_SALE_SEQUENCE_END_REASONS)[number];

export type OwnerSaleSequence = {
  status: "Aktívna" | "Ukončená";
  currentStep: number;
  completedContacts: number;
  endReason?: OwnerSaleSequenceEndReason;
  endedAt?: string;
  lastResult?: string;
  lastNote?: string;
};

export type ListingFollowUpSequence = {
  status: "Aktívna" | "Dokončená" | "Ukončená";
  currentStep: 1 | 2 | 3;
  firstFollowUpCompletedAt?: string;
  secondFollowUpCompletedAt?: string;
  lastMarketReviewCompletedAt?: string;
  endedAt?: string;
  endReason?: string;
};

export type ShotScoreActivityType = "meeting" | "viewing";

export type ShotScore = {
  id: string;
  caseId: string;
  activityId: string;
  activityType: ShotScoreActivityType;
  identity: boolean;
  value: boolean;
  acquisition: boolean;
  nextStep: boolean;
  score: 0 | 25 | 50 | 75 | 100;
  completedAt: string;
};

export const DEBRIEF_NOT_REQUIRED_REASONS = [
  "meeting_cancelled",
  "meeting_rescheduled",
  "client_no_show",
  "no_useful_interaction",
  "duplicate_event",
  "personal_non_business",
  "other"
] as const;

export type DebriefNotRequiredReason = (typeof DEBRIEF_NOT_REQUIRED_REASONS)[number];
export type DebriefEventType =
  | "property_viewing"
  | "acquisition_meeting"
  | "client_meeting"
  | "financial_meeting"
  | "negotiation"
  | "follow_up_meeting";
export type DebriefReminderStatus =
  | "scheduled"
  | "pending"
  | "snoozed"
  | "analyzing"
  | "review"
  | "completed"
  | "not_required";

export type DebriefAnalysis = {
  summary: string;
  positives: string[];
  missed: string[];
  nextStep: string;
  coaching: string;
  risks?: string[];
  objections?: DebriefObjection[];
  crmFacts?: DebriefCrmFact[];
  playbookSignals?: DebriefPlaybookSignal[];
  nextActionRecommendation?: DebriefNextActionRecommendation;
  shotScoreRecommendation?: {
    identity: DebriefShotScoreRecommendation;
    value: DebriefShotScoreRecommendation;
    acquisition: DebriefShotScoreRecommendation;
    nextStep: DebriefShotScoreRecommendation;
  };
};

export type DebriefShotScoreRecommendation = {
  value: boolean;
  evidence: string;
  status?: DebriefEvidenceStatus;
  explanation?: string;
};

export type DebriefEvidenceStatus = "confirmed" | "missed" | "unclear";
export type DebriefObjectionCategory =
  | "SELF_SALE"
  | "NO_AGENCY"
  | "BAD_AGENT_EXPERIENCE"
  | "COMMISSION"
  | "NET_PROCEEDS"
  | "LEGAL_SERVICES"
  | "MULTIPLE_AGENCIES"
  | "UNREALISTIC_PRICE"
  | "FINANCING"
  | "DECISION_MAKER"
  | "OTHER";

export type DebriefObjection = {
  category: DebriefObjectionCategory;
  wording: string;
  evidence: string;
};

export type DebriefCrmFact = {
  id: string;
  kind: "motivation" | "timeline" | "price_expectation" | "decision_maker" | "agency" | "self_sale" | "financing" | "property_condition" | "concern" | "other";
  label: string;
  value: string;
  evidence: string;
};

export type DebriefPlaybookSignal = {
  key: string;
  label: string;
  status: DebriefEvidenceStatus;
  evidence: string;
};

export type DebriefNextActionRecommendation = {
  type: string;
  date?: string;
  time?: string;
  purpose: string;
  evidence: string;
  confidence: "high" | "medium" | "low";
};

export type DebriefReminder = {
  id: string;
  eventId: string;
  caseId: string;
  eventType: DebriefEventType;
  eventLabel: string;
  startAt: string;
  endAt: string;
  endTimeInferred: boolean;
  status: DebriefReminderStatus;
  visibleAt: string;
  createdAt: string;
  updatedAt: string;
  activityId?: string;
  startedAt?: string;
  analysisRequestedAt?: string;
  analysisCompletedAt?: string;
  completedAt?: string;
  debriefId?: string;
  notRequiredAt?: string;
  notRequiredReason?: DebriefNotRequiredReason;
  notRequiredNote?: string;
  debriefText?: string;
  inputMode?: "text" | "voice";
  audioDurationSeconds?: number;
  analysis?: DebriefAnalysis;
};

export type MeetingDebrief = {
  id: string;
  reminderId: string;
  eventId: string;
  caseId: string;
  activityId?: string;
  inputText: string;
  inputMode?: "text" | "voice";
  audioDurationSeconds?: number;
  analysis: DebriefAnalysis;
  approvedCrmFacts?: DebriefCrmFact[];
  confirmedNextAction?: FollowUp;
  shotScoreId?: string;
  createdAt: string;
  confirmedAt: string;
};

export type TimelineActivityType =
  | "call"
  | "follow-up"
  | "stage"
  | "note"
  | "closed"
  | "priority"
  | "system"
  | "owner-sale"
  | "shot-score"
  | "debrief";

export type TimelineActivity = {
  id: string;
  createdAt: string;
  title: string;
  body: string;
  type?: TimelineActivityType;
  outcome?: CallOutcome;
  followUp?: FollowUp;
  activityKind?: "call" | ShotScoreActivityType;
  shotScoreId?: string;
};

export type RealEstateCase = {
  id: string;
  contactName: string;
  phone: string;
  email: string;
  transactionType?: TransactionType;
  propertyAddress: string;
  city?: string;
  cityNormalized?: string;
  citySource?: CaseLocationSource;
  municipalityId?: string;
  street?: string;
  streetNormalized?: string;
  streetSource?: CaseLocationSource;
  streetId?: string;
  locality?: string;
  propertyType: PropertyType | string;
  apartmentRooms?: ApartmentRooms;
  area?: number;
  cooperationType?: CooperationType;
  askingPrice: number;
  recommendedPrice: number;
  estimatedPrice?: number;
  commissionPotential: number;
  actualCommission?: number;
  listingStartedAt?: string;
  reservationUntil?: string;
  initialListingPrice?: number;
  realizedPrice?: number;
  soldAt?: string;
  status: CaseStatus;
  stage: CaseStage;
  leadState?: "Aktívny" | "Čakajúci" | "Vyradený";
  waitingSince?: string;
  excludedAt?: string;
  exclusionReason?: string;
  lastBusinessStage?: CaseStage;
  contactCountAtExclusion?: number;
  priority?: CasePriority;
  ownerSaleSequence?: OwnerSaleSequence;
  listingFollowUpSequence?: ListingFollowUpSequence;
  shotScores?: ShotScore[];
  debriefReminders?: DebriefReminder[];
  meetingDebriefs?: MeetingDebrief[];
  marketTimeline?: MarketTimelineState;
  nextStep: string;
  followUp?: FollowUp;
  closedAt?: string;
  closingNote?: string;
  lostReason?: string;
  closedLostAt?: string;
  notes: string;
  timeline: TimelineActivity[];
  createdAt: string;
  updatedAt: string;
};
